import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import { queryKeys } from '@/lib/query-client';
import type {
  Spot,
  CreateSpotPayload,
  PaginatedResponse,
} from '@/lib/types';

export interface SpotFilters {
  page?: number;
  page_size?: number;
  location?: number;
  zone?: number;
  q?: string;
  ordering?: string;
}

export function useSpots(filters: SpotFilters = {}, enabled = true) {
  const cleanFilters = Object.fromEntries(
    Object.entries(filters).filter(([, value]) => value !== '' && value !== undefined)
  );

  return useQuery({
    queryKey: queryKeys.spots.list(cleanFilters),
    queryFn: async (): Promise<PaginatedResponse<Spot>> => {
      const response = await apiClient.get<PaginatedResponse<Spot>>(
        '/api/v1/cf-spots/',
        { params: cleanFilters }
      );
      return response.data;
    },
    enabled: enabled && (!filters.location || filters.location > 0),
  });
}

export function useCreateSpot() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateSpotPayload): Promise<Spot> => {
      const response = await apiClient.post<Spot>('/api/v1/cf-spots/', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.spots.all });
    },
  });
}

export function useUpdateSpot() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: number;
      data: Partial<CreateSpotPayload>;
    }): Promise<Spot> => {
      const response = await apiClient.patch<Spot>(`/api/v1/cf-spots/${id}/`, data);
      return response.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.spots.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.spots.detail(variables.id) });
    },
  });
}

export function useDeleteSpot() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: number): Promise<void> => {
      await apiClient.delete(`/api/v1/cf-spots/${id}/`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.spots.all });
    },
  });
}
